import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  AttachMoney,
  Group,
  Save,
  Refresh,
  EmojiEvents,
  CheckCircle,
  Cancel
} from '@mui/icons-material';

export default function SkinsManagement() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Skins configuration
  const [skinsConfig, setSkinsConfig] = useState({
    enabled: false,
    buyInAmount: 10,
    participants: [], // Array of player IDs
    holes: {}, // Object with hole numbers as keys
    payouts: []
  });

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tournament
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      // Fetch players
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      // Load existing skins configuration
      if (tournamentData.skins) {
        setSkinsConfig(tournamentData.skins);
      }
      // Don't auto-populate participants - let user select them

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        skins: skinsConfig,
        updatedAt: new Date()
      });

      alert('Skins configuration saved successfully!');
    } catch (error) {
      console.error('Error saving skins config:', error);
      alert('Error saving configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePlayerParticipation = (playerId) => {
    setSkinsConfig(prev => {
      const participants = [...prev.participants];
      const index = participants.indexOf(playerId);
      
      if (index > -1) {
        participants.splice(index, 1);
      } else {
        participants.push(playerId);
      }
      
      return { ...prev, participants };
    });
  };

  const selectAllPlayers = () => {
    // Select all available players from the organization
    const allPlayerIds = players.map(p => p.id);
    setSkinsConfig(prev => ({ ...prev, participants: [...allPlayerIds] }));
  };

  const clearAllPlayers = () => {
    setSkinsConfig(prev => ({ ...prev, participants: [] }));
  };

  const calculatePotTotal = () => {
    return skinsConfig.participants.length * skinsConfig.buyInAmount;
  };

  const getPlayerById = (playerId) => {
    return players.find(p => p.id === playerId);
  };

  const isPlayerInTournament = (playerId) => {
    // All players in the organization can participate
    return players.some(p => p.id === playerId);
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  const participatingPlayers = skinsConfig.participants
    .map(id => getPlayerById(id))
    .filter(Boolean);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/tournaments/${tournamentId}/director`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">Skins Game</Typography>
            <Typography variant="body2" color="text.secondary">
              {tournament.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveConfig}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Configuration" />
          <Tab label="Scoring" />
          <Tab label="Results" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Settings Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={skinsConfig.enabled}
                      onChange={(e) => setSkinsConfig(prev => ({ 
                        ...prev, 
                        enabled: e.target.checked 
                      }))}
                    />
                  }
                  label="Enable Skins Game"
                />

                <TextField
                  fullWidth
                  label="Buy-In Amount"
                  type="number"
                  value={skinsConfig.buyInAmount}
                  onChange={(e) => setSkinsConfig(prev => ({ 
                    ...prev, 
                    buyInAmount: parseFloat(e.target.value) || 0 
                  }))}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  sx={{ mt: 2 }}
                />

                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary.contrastText">
                    <strong>Total Pot:</strong> ${calculatePotTotal()}
                  </Typography>
                  <Typography variant="body2" color="primary.contrastText">
                    <strong>Participants:</strong> {skinsConfig.participants.length}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  All players in your organization can participate in skins. Select the ones who want to join.
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Player Selection Card */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Select Participants
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button 
                      size="small" 
                      onClick={selectAllPlayers}
                      startIcon={<CheckCircle />}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      onClick={clearAllPlayers}
                      startIcon={<Cancel />}
                      color="error"
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {players && players.length > 0 ? (
                    players.map((player) => {
                      const isParticipating = skinsConfig.participants.includes(player.id);

                      return (
                        <ListItem
                          key={player.id}
                          button
                          onClick={() => togglePlayerParticipation(player.id)}
                          sx={{
                            bgcolor: isParticipating ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <Checkbox
                            checked={isParticipating}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={`${player.firstName} ${player.lastName}`}
                            secondary={
                              <Box display="flex" gap={1} alignItems="center">
                                <Chip 
                                  label={`HCP: ${player.handicap || 0}`} 
                                  size="small"
                                />
                                {isParticipating && (
                                  <Chip 
                                    label={`Buy-In: $${skinsConfig.buyInAmount}`} 
                                    size="small"
                                    color="success"
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })
                  ) : (
                    <Alert severity="warning">
                      No players found in your organization. Please add players first.
                    </Alert>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Participating Players Summary */}
          {participatingPlayers.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Participating Players ({participatingPlayers.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    {participatingPlayers.map((player) => (
                      <Grid item xs={12} sm={6} md={4} key={player.id}>
                        <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                          <Typography variant="body1">
                            {player.firstName} {player.lastName}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip label={`HCP: ${player.handicap || 0}`} size="small" />
                            <Chip label={`$${skinsConfig.buyInAmount}`} size="small" color="success" />
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Scoring Tab */}
      {tabValue === 1 && (
        <SkinsScoring
          tournament={tournament}
          players={players}
          skinsConfig={skinsConfig}
          setSkinsConfig={setSkinsConfig}
          onSave={handleSaveConfig}
        />
      )}

      {/* Results Tab */}
      {tabValue === 2 && (
        <SkinsResults
          tournament={tournament}
          players={players}
          skinsConfig={skinsConfig}
        />
      )}
    </Container>
  );
}

// Skins Scoring Component
function SkinsScoring({ tournament, players, skinsConfig, setSkinsConfig, onSave }) {
  const [scores, setScores] = useState({});
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    // Load scores from tournament data if available
    if (tournament.scores) {
      setScores(tournament.scores);
      setLastRefresh(new Date());
    }
  }, [tournament]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      handleRefreshScores();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, tournament.id]);

  const handleRefreshScores = async () => {
    setRefreshing(true);
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournament.id));
      if (tournamentDoc.exists()) {
        const data = tournamentDoc.data();
        if (data.scores) {
          setScores(data.scores);
          setLastRefresh(new Date());
        }
      }
    } catch (error) {
      console.error('Error refreshing scores:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getPlayerById = (playerId) => {
    return players.find(p => p.id === playerId);
  };

  const calculateSkins = () => {
    const skins = {};
    const course = tournament.course;
    
    console.log('=== SKINS DEBUG START ===');
    console.log('Tournament:', tournament);
    console.log('Scores loaded:', scores);
    console.log('Skins config:', skinsConfig);
    console.log('Participants:', skinsConfig.participants);
    console.log('Course:', course);
    
    // Use default 18-hole structure if no course data
    const holes = course?.holes || Array.from({ length: 18 }, (_, i) => ({ holeNumber: i + 1, par: 4 }));
    console.log('Using holes:', holes);

    if (!scores || Object.keys(scores).length === 0) {
      console.log('No scores found in tournament data');
      return { skins: {}, totalSkinsWon: 0, skinValue: 0 };
    }

    if (!skinsConfig.participants || skinsConfig.participants.length === 0) {
      console.log('No skins participants selected');
      return { skins: {}, totalSkinsWon: 0, skinValue: 0 };
    }

    // For each hole, find the lowest score among skins participants
    for (let holeNum = 1; holeNum <= holes.length; holeNum++) {
      let lowestScore = Infinity;
      let winners = [];
      let scoresCount = 0;
      const totalParticipants = skinsConfig.participants.length;

      skinsConfig.participants.forEach(playerId => {
        const playerScores = scores[playerId] || {};
        const score = playerScores[holeNum];

        console.log(`Hole ${holeNum}, Player ${playerId}: score = ${score}`);

        if (score !== undefined && score !== null && !isNaN(score)) {
          scoresCount++;
          if (score < lowestScore) {
            lowestScore = score;
            winners = [playerId];
          } else if (score === lowestScore) {
            winners.push(playerId);
          }
        }
      });

      console.log(`Hole ${holeNum}: scoresCount=${scoresCount}, totalParticipants=${totalParticipants}, winners=${winners.length}, lowestScore=${lowestScore}`);

      // A skin can only be won if ALL participants have completed the hole
      if (scoresCount < totalParticipants) {
        // Not all players have completed this hole yet
        skins[holeNum] = {
          status: 'pending',
          completed: scoresCount,
          total: totalParticipants
        };
      } else if (scoresCount === totalParticipants) {
        // All players have completed this hole - now check for winner
        if (winners.length === 1 && lowestScore !== Infinity) {
          // Single winner - skin is won
          skins[holeNum] = {
            winner: winners[0],
            score: lowestScore,
            status: 'won'
          };
        } else if (winners.length > 1) {
          // Multiple players tied - no one wins the skin
          skins[holeNum] = {
            tied: true,
            players: winners,
            score: lowestScore,
            status: 'tied'
          };
        } else {
          // Edge case: all have scores but no clear winner
          skins[holeNum] = {
            status: 'tied'
          };
        }
      } else {
        // No scores at all
        skins[holeNum] = {
          status: 'pending',
          completed: 0,
          total: totalParticipants
        };
      }
    }

    // Count total skins won
    const totalSkinsWon = Object.values(skins).filter(s => s.status === 'won').length;
    
    // Calculate value per skin: Total Pot / Number of Skins Won
    const totalPot = skinsConfig.participants.length * skinsConfig.buyInAmount;
    const skinValue = totalSkinsWon > 0 ? totalPot / totalSkinsWon : 0;

    // Assign value to each won skin
    Object.keys(skins).forEach(holeNum => {
      if (skins[holeNum].status === 'won') {
        skins[holeNum].value = skinValue;
      }
    });

    console.log('Calculated skins:', skins);
    console.log('Total skins won:', totalSkinsWon);
    console.log('Value per skin:', skinValue);
    console.log('=== SKINS DEBUG END ===');

    return { skins, totalSkinsWon, skinValue };
  };

  const { skins, totalSkinsWon, skinValue } = calculateSkins();

  if (!skinsConfig.enabled) {
    return (
      <Alert severity="warning">
        Skins game is not enabled for this tournament. Please enable it in the Configuration tab.
      </Alert>
    );
  }

  if (skinsConfig.participants.length === 0) {
    return (
      <Alert severity="warning">
        No participants selected for skins. Please add participants in the Configuration tab.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Skins Tracking
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption">
                  Auto-refresh {autoRefresh && '(30s)'}
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleRefreshScores}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Now'}
            </Button>
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Skins are calculated in real-time based on entered scores. Auto-refresh is enabled by default (every 30 seconds).
          Click "Refresh Now" for immediate updates. <strong>A skin can only be won if ALL participants have completed that hole.</strong> Only the lowest score wins a skin - ties mean no one wins that hole. All skins have equal value: Total Pot ÷ Number of Skins Won.
        </Alert>
        
        {!tournament.course && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>No course data found.</strong> Skins will work with a default 18-hole structure. 
            For better tracking, consider adding a golf course to this tournament.
          </Alert>
        )}
        
        {totalSkinsWon > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>{totalSkinsWon} skin(s) won so far!</strong> Each skin is worth ${skinValue.toFixed(2)}
          </Alert>
        )}
        
        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Hole</strong></TableCell>
                <TableCell><strong>Par</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Winner</strong></TableCell>
                <TableCell align="right"><strong>Value</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 18 }, (_, i) => i + 1).map(holeNum => {
                const holes = tournament.course?.holes || Array.from({ length: 18 }, (_, i) => ({ holeNumber: i + 1, par: 4 }));
                const hole = holes.find(h => h.holeNumber === holeNum);
                const skin = skins[holeNum];

                return (
                  <TableRow key={holeNum}>
                    <TableCell>{holeNum}</TableCell>
                    <TableCell>{hole?.par || '-'}</TableCell>
                    <TableCell>
                      {skin?.status === 'won' ? (
                        <Chip label="Won" color="success" size="small" />
                      ) : skin?.status === 'tied' ? (
                        <Chip label="Tied (No Winner)" color="warning" size="small" />
                      ) : skin?.status === 'pending' && skin?.completed !== undefined ? (
                        <Chip 
                          label={`Waiting (${skin.completed}/${skin.total})`} 
                          color="info" 
                          size="small" 
                        />
                      ) : (
                        <Chip label="Pending" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {skin?.winner ? (
                        <Box>
                          {(() => {
                            const player = getPlayerById(skin.winner);
                            return player ? `${player.firstName} ${player.lastName}` : 'Unknown';
                          })()}
                          <Typography variant="caption" display="block" color="text.secondary">
                            Score: {skin.score}
                          </Typography>
                        </Box>
                      ) : skin?.tied ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {skin.players.slice(0, 3).map((playerId, index) => {
                              const player = getPlayerById(playerId);
                              const playerName = player ? `${player.firstName} ${player.lastName}` : 'Unknown';
                              return (
                                <span key={playerId}>
                                  {playerName}
                                  {index < Math.min(3, skin.players.length) - 1 ? ', ' : ''}
                                </span>
                              );
                            })}
                            {skin.players.length > 3 && '...'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            ({skin.players.length} players tied)
                          </Typography>
                        </Box>
                      ) : skin?.status === 'pending' && skin?.completed !== undefined ? (
                        <Typography variant="caption" color="text.secondary">
                          Waiting for {skin.total - skin.completed} more player{skin.total - skin.completed !== 1 ? 's' : ''}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {skin?.status === 'won' && skin?.value ? (
                        <Chip 
                          label={`$${skin.value.toFixed(2)}`} 
                          color="success" 
                          size="small"
                          icon={<AttachMoney />}
                        />
                      ) : skin?.status === 'tied' ? (
                        <Typography variant="caption" color="text.secondary">No payout</Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

// Skins Results Component
function SkinsResults({ tournament, players, skinsConfig }) {
  const [scores, setScores] = useState({});
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    // Load scores from tournament data if available
    if (tournament.scores) {
      setScores(tournament.scores);
      setLastRefresh(new Date());
    }
  }, [tournament]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      handleRefreshScores();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, tournament.id]);

  const handleRefreshScores = async () => {
    setRefreshing(true);
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournament.id));
      if (tournamentDoc.exists()) {
        const data = tournamentDoc.data();
        if (data.scores) {
          setScores(data.scores);
          setLastRefresh(new Date());
        }
      }
    } catch (error) {
      console.error('Error refreshing scores:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getPlayerById = (playerId) => {
    return players.find(p => p.id === playerId);
  };

  const calculateResults = () => {
    const results = {};
    const currentScores = scores || {};
    
    // Use default 18-hole structure if no course data
    const holes = tournament.course?.holes || Array.from({ length: 18 }, (_, i) => ({ holeNumber: i + 1, par: 4 }));
    console.log('Results - Using holes:', holes);

    // Calculate skins for each hole
    const skins = {};
    for (let holeNum = 1; holeNum <= holes.length; holeNum++) {
      let lowestScore = Infinity;
      let winners = [];
      let scoresCount = 0;
      const totalParticipants = skinsConfig.participants.length;

      skinsConfig.participants.forEach(playerId => {
        const playerScores = currentScores[playerId] || {};
        const score = playerScores[holeNum];

        if (score !== undefined && score !== null && !isNaN(score)) {
          scoresCount++;
          if (score < lowestScore) {
            lowestScore = score;
            winners = [playerId];
          } else if (score === lowestScore) {
            winners.push(playerId);
          }
        }
      });

      // A skin can only be won if ALL participants have completed the hole
      if (scoresCount < totalParticipants) {
        // Not all players have completed this hole yet
        skins[holeNum] = {
          status: 'pending',
          completed: scoresCount,
          total: totalParticipants
        };
      } else if (scoresCount === totalParticipants) {
        // All players have completed this hole - now check for winner
        if (winners.length === 1 && lowestScore !== Infinity) {
          // Single winner - skin is won
          skins[holeNum] = {
            winner: winners[0],
            score: lowestScore,
            status: 'won'
          };
        } else if (winners.length > 1) {
          // Multiple players tied - no one wins the skin
          skins[holeNum] = {
            tied: true,
            players: winners,
            score: lowestScore,
            status: 'tied'
          };
        } else {
          // Edge case: all have scores but no clear winner
          skins[holeNum] = {
            status: 'tied'
          };
        }
      } else {
        // No scores at all
        skins[holeNum] = {
          status: 'pending',
          completed: 0,
          total: totalParticipants
        };
      }
    }

    // Count total skins won
    const totalSkinsWon = Object.values(skins).filter(s => s.status === 'won').length;
    
    // Calculate value per skin: Total Pot / Number of Skins Won
    const totalPot = skinsConfig.participants.length * skinsConfig.buyInAmount;
    const skinValue = totalSkinsWon > 0 ? totalPot / totalSkinsWon : 0;

    // Build results and assign value to each won skin
    Object.keys(skins).forEach(holeNum => {
      if (skins[holeNum].status === 'won') {
        skins[holeNum].value = skinValue;
        
        const winnerId = skins[holeNum].winner;
        if (!results[winnerId]) {
          results[winnerId] = { skinsWon: 0, totalWinnings: 0, holes: [] };
        }
        results[winnerId].skinsWon += 1;
        results[winnerId].totalWinnings += skinValue;
        results[winnerId].holes.push({ hole: parseInt(holeNum), value: skinValue });
      }
    });

    return { results, skins, totalSkinsWon, skinValue };
  };

  const { results, skins, totalSkinsWon, skinValue } = calculateResults();
  const totalPot = skinsConfig.participants.length * skinsConfig.buyInAmount;
  const paidOut = totalSkinsWon > 0 ? totalPot : 0; // All pot is paid out if any skins are won

  const sortedResults = Object.entries(results)
    .sort(([, a], [, b]) => b.totalWinnings - a.totalWinnings);

  return (
    <Grid container spacing={3}>
      {/* Refresh Button */}
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption">
                  Auto-refresh {autoRefresh && '(30s)'}
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefreshScores}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Now'}
          </Button>
        </Box>
      </Grid>

      {/* Summary Card */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Pot
                </Typography>
                <Typography variant="h5" color="primary">
                  ${totalPot}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Skins Won
                </Typography>
                <Typography variant="h5" color="info.main">
                  {totalSkinsWon}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Value Per Skin
                </Typography>
                <Typography variant="h5" color="success.main">
                  ${skinValue.toFixed(2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Remaining Holes
                </Typography>
                <Typography variant="h5" color={18 - Object.keys(skins).length > 0 ? 'warning.main' : 'success.main'}>
                  {18 - Object.keys(skins).length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Participants
                </Typography>
                <Typography variant="h5">
                  {skinsConfig.participants.length}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Winners List */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Winners
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {sortedResults.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Rank</strong></TableCell>
                      <TableCell><strong>Player</strong></TableCell>
                      <TableCell align="center"><strong>Skins Won</strong></TableCell>
                      <TableCell align="right"><strong>Total Winnings</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedResults.map(([playerId, result], index) => {
                      const player = getPlayerById(playerId);
                      return (
                        <TableRow key={playerId}>
                          <TableCell>
                            {index === 0 ? (
                              <EmojiEvents color="warning" />
                            ) : (
                              <Typography>{index + 1}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1">
                              {player ? `${player.firstName} ${player.lastName}` : 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Holes: {result.holes.sort((a, b) => a.hole - b.hole).map(h => h.hole).join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={result.skinsWon} color="primary" />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`$${result.totalWinnings}`} 
                              color="success"
                              icon={<AttachMoney />}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No skins have been won yet. Scores need to be entered in the Scorer Interface.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Detailed Breakdown */}
      {sortedResults.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detailed Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {sortedResults.map(([playerId, result]) => {
                const player = getPlayerById(playerId);
                return (
                  <Box key={playerId} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {player ? `${player.firstName} ${player.lastName}` : 'Unknown'} - ${result.totalWinnings.toFixed(2)}
                    </Typography>
                    <Grid container spacing={1}>
                      {result.holes.sort((a, b) => a.hole - b.hole).map(({ hole, value }) => (
                        <Grid item key={hole}>
                          <Chip 
                            label={`Hole ${hole}: $${value.toFixed(2)}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}

